import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Sparkles, Search, Loader2, History, Trash2, Clock, ExternalLink } from "lucide-react";

const EXAMPLES = ["docenti", "presidenti", "medici", "vuole pagare a rate", "ci ripensa a settembre"];

export default function AiSearch() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [fromHistory, setFromHistory] = useState(false);
  const navigate = useNavigate();

  const loadHistory = async () => {
    try {
      const res = await api.get("/ai-searches");
      setHistory(res.data.items || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { loadHistory(); }, []);

  const run = async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setQuery(term);
    setLoading(true);
    setError(null);
    setFromHistory(false);
    try {
      const res = await api.post("/leads/ai-search", { query: term });
      setData(res.data);
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || "Ricerca non riuscita");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (item) => {
    setLoading(true);
    setError(null);
    setQuery(item.query);
    try {
      const res = await api.get(`/ai-searches/${item.id}`);
      setData(res.data);
      setFromHistory(true);
    } catch (e) {
      setError("Impossibile caricare la ricerca salvata");
    } finally {
      setLoading(false);
    }
  };

  const deleteHistory = async (e, item) => {
    e.stopPropagation();
    await api.delete(`/ai-searches/${item.id}`);
    loadHistory();
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-6" data-testid="ai-search-page">
      <div className="flex items-start gap-3 rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900">
        <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Ricerca IA nelle note</p>
          <p className="mt-1 text-violet-800">
            Scrivi cosa cerchi con parole tue (es. "docenti", "vuole pagare a rate", "ci ripensa a settembre").
            L'IA <strong>legge e capisce le note dei commerciali</strong> (e la professione) e ti mostra a quali
            lead corrispondono — ragionando sul significato, non su una singola parola.
          </p>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              data-testid="ai-search-input"
              placeholder="Descrivi cosa cerchi nelle note…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              className="flex-1"
            />
            <Button data-testid="ai-search-button" onClick={() => run()} disabled={loading} className="gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Cerca con IA
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Esempi:</span>
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                data-testid={`ai-search-example-${ex}`}
                onClick={() => run(ex)}
                className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
              >
                {ex}
              </button>
            ))}
          </div>
          {loading && (
            <p className="mt-3 text-xs text-muted-foreground">L'IA sta leggendo le note dei lead… può richiedere qualche secondo.</p>
          )}
        </CardContent>
      </Card>

      {/* Cronologia ricerche salvate */}
      {history.length > 0 && (
        <Card className="shadow-sm" data-testid="ai-search-history-card">
          <CardContent className="p-5">
            <div className="mb-3 flex items-center gap-2">
              <History className="h-5 w-5 text-violet-500" />
              <h3 className="font-display text-base font-semibold tracking-tight">Ricerche salvate</h3>
              <span className="text-xs text-muted-foreground">— clicca per riaprire i risultati senza rieseguire l'IA</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {history.map((h) => (
                <div
                  key={h.id}
                  data-testid="ai-search-history-item"
                  onClick={() => openHistory(h)}
                  className="group flex cursor-pointer items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1.5 text-sm transition-colors hover:bg-accent"
                >
                  <span className="font-medium">{h.query}</span>
                  <Badge className="bg-violet-100 text-violet-700">{h.count}</Badge>
                  <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
                    <Clock className="h-3 w-3" />{fmt(h.updated_at)}
                  </span>
                  <button
                    data-testid="ai-search-history-delete"
                    onClick={(e) => deleteHistory(e, h)}
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-rose-600 group-hover:opacity-100"
                    title="Elimina dalla cronologia"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="ai-search-error">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-3" data-testid="ai-search-results">
          <p className="text-sm font-medium">
            {data.count} lead trovati per “{data.query}”
            {typeof data.analyzed === "number" && (
              <span className="ml-1 font-normal text-muted-foreground">— {data.analyzed} lead analizzati dall'IA</span>
            )}
            {fromHistory && (
              <Badge className="ml-2 bg-violet-100 text-violet-700" data-testid="ai-search-from-history">dalla cronologia</Badge>
            )}
          </p>

          <Card className="overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/60">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Lead</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Professione</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Commerciale</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Perché corrisponde (IA)</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Scheda Pipedrive</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Nessun lead trovato</TableCell></TableRow>
                  ) : (
                    data.items.map((it) => (
                      <TableRow
                        key={it.lead_id}
                        data-testid="ai-search-result-row"
                        className="cursor-pointer hover:bg-secondary/40"
                        onClick={() => navigate(`/leads/${it.lead_id}`)}
                      >
                        <TableCell>
                          <div className="font-medium">{it.name || it.email || "—"}</div>
                          <div className="text-xs text-muted-foreground">{it.email}</div>
                        </TableCell>
                        <TableCell className="text-sm">{it.profession || "—"}</TableCell>
                        <TableCell className="text-sm">{it.commercial || "—"}</TableCell>
                        <TableCell className="max-w-[380px]">
                          {it.motivo && <Badge className="bg-violet-100 text-violet-700">{it.motivo}</Badge>}
                          {it.snippet && <p className="mt-1 truncate text-xs text-muted-foreground" title={it.snippet}>{it.snippet}</p>}
                        </TableCell>
                        <TableCell>
                          {it.pipedrive_url ? (
                            <a
                              href={it.pipedrive_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="ai-search-pipedrive-link"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
                            >
                              <ExternalLink className="h-3.5 w-3.5" /> Apri
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
